import FocusLogModal, { type FocusPrefill } from '@/components/focus/FocusLogModal';
import FocusActionModal from '@/components/mirror/FocusActionModal';
import { supabase } from '@/integrations/supabase/client';
import { MIRROR_FOCUS_CARDS_ENABLED } from '@/lib/featureFlags';
import { focusOriginOperationId, focusOwnerSession, focusUuid, type FocusActionPrefill } from '@/lib/focusExecutor';
import type { CaptureContext, ModuleCaptureHandler } from '@/lib/capture/types';

async function origin(ctx: CaptureContext, actionId: string, action: FocusActionPrefill['action'], input: Record<string, unknown>): Promise<FocusActionPrefill | null> {
  // Model output supplies draft fields only. The review obtains original
  // snapshots, ownership and the server's clock independently.
  const args = structuredClone(input), owner = ctx.ownerId, session = ctx.sessionId;
  if (!focusUuid(owner) || !focusUuid(session)) return null;
  const assert = () => ctx.assertCurrent?.(); assert();
  const operation_id = await focusOriginOperationId(owner, session, actionId, 'operation', assert); assert();
  const target = action === 'create' ? await focusOriginOperationId(owner, session, actionId, 'focus', assert)
    : action === 'start_timer' ? await focusOriginOperationId(owner, session, actionId, 'timer', assert)
    : focusUuid(args.focus_session_id) ? args.focus_session_id.toLowerCase() : undefined;
  assert();
  if (['update', 'delete'].includes(action) && !target) return null;
  const fields = action === 'delete' ? [] : action === 'start_timer' ? ['task', 'duration_minutes']
    : ['create', 'update'].includes(action) ? ['task', 'duration_minutes', 'mode', 'focus_quality', 'completed_at'] : [];
  const draft = Object.fromEntries(Object.entries(args).filter(([key]) => fields.includes(key)));
  return { ...draft, action, action_id: actionId, operation_id, expected_user_id: owner, expected_session_id: session,
    ...(action === 'start_timer' ? { timer_id: target } : ['create', 'update', 'delete'].includes(action) ? { focus_session_id: target } : {}),
    ...(action === 'create' ? { source: 'mirror' as const } : {}) } as FocusActionPrefill;
}

export const focusCaptureHandler: ModuleCaptureHandler<FocusPrefill> = {
  tool: 'propose_focus_log', isEnabled: () => MIRROR_FOCUS_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === 'propose_focus_log' && typeof proposal.args.raw_text === 'string' ? proposal.args.raw_text.trim() : '',
  extract: async (rawText, proposal, ctx) => {
    if (proposal.tool !== 'propose_focus_log' || !rawText || !focusUuid(ctx.ownerId) || !focusUuid(ctx.sessionId)) return null;
    const owner = ctx.ownerId, session = ctx.sessionId, actionId = proposal.action_id, transcript = String(rawText), language = ctx.lang;
    const assert = () => ctx.assertCurrent?.(); assert();
    const pinned = await focusOwnerSession(owner, assert); assert();
    const { data, error } = await supabase.functions.invoke('focus-extract', {
      body: { transcript, language }, headers: { Authorization: 'Bearer ' + pinned.access_token },
    }); assert();
    await focusOwnerSession(owner, assert); assert();
    if (error) { await ctx.surfaceQuota(error); assert(); return null; }
    const r = data as { task?: unknown; duration_minutes?: unknown; confidence?: unknown } | null;
    if (!r || typeof r.task !== 'string' || !r.task.trim() || r.task.trim().length > 1000 || /\p{Cc}/u.test(r.task)
      || typeof r.duration_minutes !== 'number' || !Number.isFinite(r.duration_minutes)
      || typeof r.confidence !== 'number' || !Number.isFinite(r.confidence) || r.confidence < .25) return null;
    const duration = Math.round(r.duration_minutes);
    if (duration < 1 || duration > 1440) return null;
    const operation_id = await focusOriginOperationId(owner, session, actionId, 'operation', assert); assert();
    const focus_session_id = await focusOriginOperationId(owner, session, actionId, 'focus', assert); assert();
    return { task: r.task.trim(), duration_minutes: duration, source: 'voice', transcriptRaw: transcript,
      expected_user_id: owner, expected_session_id: session, action_id: actionId, operation_id, focus_session_id,
      client_action_id: 'mirror_focus_report_' + operation_id };
  },
  renderModal: props => <FocusLogModal {...props} />,
};

export const focusActionCaptureHandler: ModuleCaptureHandler<FocusActionPrefill> = {
  tool: 'propose_focus_action', isEnabled: () => MIRROR_FOCUS_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === 'propose_focus_action' ? JSON.stringify(proposal.args) : '',
  extract: async (_raw, proposal, ctx) => proposal.tool === 'propose_focus_action' && ['create', 'update', 'delete'].includes(proposal.args.action)
    ? origin(ctx, proposal.action_id, proposal.args.action, proposal.args as unknown as Record<string, unknown>) : null,
  renderModal: props => <FocusActionModal {...props} />,
};

export const focusTimerCaptureHandler: ModuleCaptureHandler<FocusActionPrefill> = {
  tool: 'propose_focus_timer_action', isEnabled: () => MIRROR_FOCUS_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === 'propose_focus_timer_action' ? JSON.stringify(proposal.args) : '',
  extract: async (_raw, proposal, ctx) => {
    if (proposal.tool !== 'propose_focus_timer_action' || !['start', 'pause', 'resume', 'reset'].includes(proposal.args.action)) return null;
    return origin(ctx, proposal.action_id, `${proposal.args.action}_timer` as FocusActionPrefill['action'], proposal.args as unknown as Record<string, unknown>);
  },
  renderModal: props => <FocusActionModal {...props} />,
};
